
import { DateFormat, Locale, TimeFormat } from '@neumatter/intl'
import SlotMap from '@neumatter/slot-map'
import DateTime, { yearToISOString } from './DateTime.js'
import Duration from './Duration.js'
import { getOption } from './helpers/utils.js'
import { parsePlainDate } from './helpers/parsers.js'
import Calendar from './Calendar.js'
import { regulateISODate, rejectISODate } from './helpers/regulate.js'

const SYMBOL_IS_PLAIN_DATE = Symbol.for('neumatter.DateTime.PlainDate.isPlainDate')

export default class PlainDate {
  static now (options = { timeZone: 'UTC', calendar: 'iso8601' }) {
    const isoDate = DateTime.now(options)
    const slotMap = SlotMap.get(isoDate)

    return new PlainDate(
      slotMap.get('year'),
      slotMap.get('month'),
      slotMap.get('day'),
      slotMap.get('calendar')
    )
  }

  static compare (a, b) {
    a = PlainDate.isPlainDate(a) ? a.getISOFields() : PlainDate.from(a).getISOFields()
    b = PlainDate.isPlainDate(b) ? b.getISOFields() : PlainDate.from(b).getISOFields()

    if (a.year !== b.year) {
      return a.year < b.year ? -1 : 1
    } else if (a.month !== b.month) {
      return a.month < b.month ? -1 : 1
    } else if (a.day !== b.day) {
      return a.day < b.day ? -1 : 1
    }

    return 0
  }

  static isPlainDate (input) {
    return (
      typeof input === 'object' &&
      typeof input[SYMBOL_IS_PLAIN_DATE] === 'boolean' &&
      input[SYMBOL_IS_PLAIN_DATE]
    )
  }

  static from (item, options) {
    const opts = Object.create(options === undefined ? {} : options)
    const overflow = getOption(
      opts,
      'overflow',
      'string',
      ['constrain', 'reject'],
      'constrain'
    )

    switch (typeof item) {
      case 'string': {
        let {
          year,
          month,
          day,
          calendar
        } = parsePlainDate(item)

        if (overflow === 'constrain') {
          ;({ year, month, day } = regulateISODate(year, month, day))
        } else {
          rejectISODate(year, month, day)
        }

        return new PlainDate(
          year,
          month,
          day,
          calendar
        )
      }

      case 'bigint': {
        return DateTime.from(item, { overflow }).toPlainDate()
      }

      case 'object': {
        if (DateTime.isDateTime(item)) {
          return item.toPlainDate()
        } else if (
          'year' in item ||
          'month' in item ||
          'day' in item ||
          'calendar' in item
        ) {
          return Calendar.from(item.calendar).dateFromFields(item, { overflow })
        } else {
          throw new RangeError(`Could not parse ${item}`)
        }
      }

      default:
        throw new RangeError(`Could not parse ${item}`)
    }
  }

  constructor (isoYear, isoMonth, isoDay, calendar) {
    rejectISODate(isoYear, isoMonth, isoDay)
    const slotMap = SlotMap.get(this)
    slotMap.set('year', isoYear)
    slotMap.set('month', isoMonth)
    slotMap.set('day', isoDay)
    slotMap.set('calendar', Calendar.from(calendar))
  }

  get [SYMBOL_IS_PLAIN_DATE] () {
    return true
  }

  /**
   *
   * @type {Calendar}
   */
  get calendar () {
    return SlotMap.getInternal(this, 'calendar')
  }

  get year () {
    return this.calendar.year(this)
  }

  get month () {
    return this.calendar.month(this)
  }

  get monthCode () {
    return this.calendar.monthCode(this)
  }

  get day () {
    return this.calendar.day(this)
  }

  get era () {
    return this.calendar.era(this)
  }

  get eraYear () {
    return this.calendar.eraYear(this)
  }

  get dayOfWeek () {
    return this.calendar.dayOfWeek(this)
  }

  get dayOfYear () {
    return this.calendar.dayOfYear(this)
  }

  get weekOfYear () {
    return this.calendar.weekOfYear(this)
  }

  get daysInWeek () {
    return this.calendar.daysInWeek(this)
  }

  get daysInYear () {
    return this.calendar.daysInYear(this)
  }

  get daysInMonth () {
    return this.calendar.daysInMonth(this)
  }

  get monthsInYear () {
    return this.calendar.monthsInYear(this)
  }

  get inLeapYear () {
    return this.calendar.inLeapYear(this)
  }

  with (plainDateLike) {
    let { year, month, day } = this.getISOFields()

    if ('year' in plainDateLike) {
      year = plainDateLike.year
    }

    if ('month' in plainDateLike) {
      month = plainDateLike.month
    }

    if ('day' in plainDateLike) {
      day = plainDateLike.day
    }

    return new PlainDate(year, month, day, this.calendar)
  }

  withCalendar (calendar = 'iso8601') {
    const { year, month, day } = this.getISOFields()
    return new PlainDate(
      year,
      month,
      day,
      calendar
    )
  }

  add (durationLike) {
    const duration = Duration.from(durationLike)
    const addedDate = this.calendar.dateAdd(this, duration)
    const addedDateSlots = SlotMap.get(addedDate)

    return new PlainDate(
      addedDateSlots.get('year'),
      addedDateSlots.get('month'),
      addedDateSlots.get('day'),
      this.calendar
    )
  }

  subtract (durationLike) {
    const duration = Duration.from(durationLike).negate()
    const addedDate = this.calendar.dateAdd(this, duration)
    const addedDateSlots = SlotMap.get(addedDate)

    return new PlainDate(
      addedDateSlots.get('year'),
      addedDateSlots.get('month'),
      addedDateSlots.get('day'),
      this.calendar
    )
  }

  until (plainDate, options) {
    return Duration.from(this.calendar.dateUntil(this, plainDate, options))
  }

  since (plainDate, options) {
    plainDate = PlainDate.isPlainDate(plainDate) ? plainDate : PlainDate.from(plainDate)
    return Duration.from(this.calendar.dateUntil(plainDate, this, options))
  }

  getISOFields () {
    const slotMap = SlotMap.get(this)

    return {
      year: slotMap.get('year'),
      month: slotMap.get('month'),
      day: slotMap.get('day'),
      calendar: this.calendar
    }
  }

  toDateTime (relativeTo) {
    relativeTo = DateTime.isDateTime(relativeTo)
      ? relativeTo
      : DateTime.from(relativeTo)
    const { hour, minute, second, millisecond, microsecond, nanosecond, timeZone } = relativeTo
    const { year, month, day, calendar } = this.getISOFields()

    return new DateTime(
      year,
      month,
      day,
      hour,
      minute,
      second,
      millisecond,
      microsecond,
      nanosecond,
      timeZone,
      calendar
    )
  }

  toLocaleString (locales = Locale.systemLocale, options = {}) {
    if (!('calendar' in options) && this.calendar.id !== 'iso8601') {
      options.calendar = this.calendar.id
    }

    return new DateFormat(locales, options).format(this)
  }

  toString (options) {
    const slotMap = SlotMap.get(this)
    const opts = Object.create(options === undefined ? {} : options)
    const year = yearToISOString(slotMap.get('year'))
    const month = `00${slotMap.get('month')}`.slice(-2)
    const day = `00${slotMap.get('day')}`.slice(-2)

    let result = `${year}-${month}-${day}`

    const calendarName = getOption(
      opts,
      'timeZoneName',
      'string',
      ['auto', 'always', 'never'],
      'auto'
    )

    if (
      calendarName === 'always' || (
        calendarName === 'auto' && this.calendar.id !== 'iso8601'
      )
    ) {
      result += `[u-ca=${this.calendar.id}]`
    }

    return result
  }

  toJSON () {
    return this.toString()
  }

  toBSON () {
    return this.toString()
  }

  [Symbol.for('nodejs.util.inspect.custom')] () {
    return `PlainDate(\x1b[35m${this.toString()}\x1b[0m)`
  }

  inspect () {
    return `PlainDate(${this.toString()})`
  }
}

/*
const getTimeSince = Duration.createTimeOrigin()

console.log(new PlainDate(2023, 4, 1).until(new PlainDate(2023, 4, 12)))
console.log(new PlainDate(2023, 4, 12).since(new PlainDate(2023, 4, 1)))
console.log(new PlainDate(2023, 4, 1).add({ days: 11 }))
console.log(new PlainDate(2023, 4, 1, 'hebrew').until(new PlainDate(2023, 4, 12, 'hebrew')))
console.log('add', new PlainDate(2023, 4, 1, 'hebrew').add({ days: 11 }))
console.log(DateTime.now().with({ day: 1 }).until(DateTime.now().with({ day: 12 })))
console.log(DateTime.now().with({ day: 12 }).since(DateTime.now().with({ day: 1 })))
console.log(PlainDate.now())
console.log(PlainDate.now({ timeZone: 'America/Chicago' }))
console.log(PlainDate.from('2023-04-12[u-ca=hebrew]'))
console.log(PlainDate.from({ year: 5779, monthCode: 'M05L', day: 18, calendar: 'hebrew' }))
console.log(PlainDate.from({ year: 5779, monthCode: 'M05L', day: 18, calendar: 'hebrew' }).toLocaleString())
const tf = new TimeFormat()
console.log(tf.resolvedOptions())
console.log(DateTime.now({ timeZone: 'America/Detroit' }).toPlainTime().toLocaleString())
console.log(getTimeSince())
*/
